import businessApi from '@/api/services/business'
import Util from '@/util'
import Constants from '@/util/constants'

const defaultState = () => {
  return {
    business: {},
    businessVO: {},
    businessBrands: [],
    paymentInfo: [],
    outletId: null,
    businessId: null,
    businessError: false,
    resourceId: null,
    resourceList: {},
    query: null,
    features: {}
  }
}

const state = Object.assign({
  listingVOs: [],
  listingDiscounts: [],
  otherOutlets: []
}, defaultState())

const getters = {
  outletId() {
    return state.outletId;
  },

  businessId () {
    return state.business.mid
  },

  currency () {
    return state.business.currency
  },

  channelDefId () {
    if (state.business.onlineListing) {
      return state.business.onlineListing.channelDefId
    }
    return null
  },

  branchId () {
    if (state.business.onlineListing) {
      return state.business.onlineListing.branchId
    }
    return null
  },

  billRoundingMode () {
    return state.business.billRoundingMode
  },

  transactionEnd () {
    return state.business.transactionEnd
  },

  isPaymentUnavailable () {
    return !Util.isMobile() && !state.paymentInfo.some(pay => pay.name !== 'UPI')
  },

  deliveryModes () {
    if (state.business.onlineListing) {
      return state.business.onlineListing.deliveryModes
    }
    return null
  },

  deliveryModePreferences: (state) => (mode) => {
    const onlineListing = state.business.onlineListing;
    let pref = null;

    switch (mode) {
      case Constants.dataMap.deliveryMode.DELIVERY:
        pref = onlineListing.deliveryPreferences;
        break;
      case Constants.dataMap.deliveryMode.PICKUP:
        pref = onlineListing.pickupPreferences;
        break;
    }
    return pref;
  },

  mapConfig() {
    const mapConfig = {};
    if (state.business.onlineListing) {
      mapConfig.outletName = state.business.brandName || state.business.businessName;
      mapConfig.countryCode = state.business.address.countryCode;
      mapConfig.geolocation = state.business.address.geolocation;
      if (state.business.onlineListing.deliveryPreferences) {
        mapConfig.maxDistance = state.business.onlineListing.deliveryPreferences.maxDistance;
        mapConfig.distanceUnit = state.business.onlineListing.deliveryPreferences.distanceUnit;
      }
    } else if (state.businessVO) {
      mapConfig.outletName = state.businessVO.name
      mapConfig.countryCode = state.businessVO.countryCode
    }
    return mapConfig;
  },

  hasBothDeliveryModes() {
    const deliveryModes = state.business.onlineListing.deliveryModes;
    if (Array.isArray(deliveryModes) &&
        deliveryModes.length === 2) {
      return true;
    }
    return false;
  },

  pickupPreferences () {
    if (state.business.onlineListing && state.business.onlineListing.pickupPreferences) {
      return state.business.onlineListing.pickupPreferences
    }
    return null
  },

  isBizInactive: (state) => {
    if (!state.business.onlineListing || !state.business.onlineListing.status || state.business.onlineListing.status === 'Inactive') {
      return true
    }
    if (state.resourceId) {
      return !getters.resource(state.resourceId)
    }
    return false
  },

  isBizClosed: (state, getters) => {
    const daysArr = Constants.dataMap.daysArr
    const bizDays = state.business.onlineListing.businessHours.businessDays
    const today = new Date()
    const todaysHours = bizDays.find(dayObj => {
      return dayObj.day === daysArr[today.getDay()]
    })

    return todaysHours.slots.every(slot => {
      const now = new Date();
      const start = Util.convertToDate(slot.startTime);
      const end = Util.convertToDate(slot.endTime);
      return !(start <= now && now < end);
    })
  },

  timeZone: (state) => {
    return state.business.timeZone || { zoneId: 'Asia/Calcutta' }
  },

  resource: (state) => (rid) => {
    if (!rid) {
      return null
    }

    return state.resourceList[rid]
  },

  query: (state) => {
    if (Util.isEmpty(state.query)) {
      return null
    }
    return state.query
  },

  businessContact: (state) => {
    const onlineListing = state.business.onlineListing

    if (onlineListing && onlineListing.contactInfo && onlineListing.contactInfo.phoneNumber) {
      return onlineListing.contactInfo.phoneNumber
    }
    return null
  },

  isOrderedInBizHours: (state) => (dateObj) => {
    const daysArr = Constants.dataMap.daysArr
    const bizDays = state.business.onlineListing.businessHours.businessDays
    const date = new Date(dateObj)
    const todaysHours = bizDays.find(dayObj => {
      return dayObj.day === daysArr[date.getDay()]
    })

    return todaysHours.slots.some(slot => {
      let now = new Date();
      const start = Util.convertToDate(slot.startTime);
      const end = Util.convertToDate(slot.endTime);
      now.setHours(date.getHours())
      now.setMinutes(date.getMinutes())
      return start <= now && now <= end;
    })
  },

  getListingVOById: (state) => (outletId) => {
    if (!state.listingVOs) {
      return null;
    }
    return state.listingVOs.find(ovo => ovo.listingId === outletId)
  },

  outletAddress: (state) => {
    if (state.business.address) {
      return state.business.address
    }
    return null;
  }
}
const actions = {
  async getOutlet ({ commit, state }, options) {
    const params = {
      outletId: state.outletId
    }
    await businessApi.getOutlet(params).then(business => {
      commit('setBusiness', business)
      if (business.onlineListing.paymentInfo) {
        commit('setPaymentInfo', business.onlineListing.paymentInfo)
      }
      if (business.onlineListing.extras) {
        commit('setFeatures', business.onlineListing.extras)
      }
      if (business.onlineListing.status === 'Inactive') {
        commit('setBusinessError', true)
      }
      if (business.resourceList) {
        commit('setResourceList', Util.arrayToMap(business.resourceList, 'resourceId'))
      }
    })
  },

  updateOutletId ({ commit, dispatch }, outletId) {
    dispatch('user/updateVisitDate', {
      outletId,
      visitDate: new Date().toISOString()
    }, { root: true });
    commit('updateOutletId', outletId)
  },

  getBusiness ({ commit, state }, options) {
    const params = {
      businessId: state.businessId
    }
    const businessBrands = (listings) => {
      const brands = Util.removeArrFalsyVal(Util.unique(Util.pluck(listings, ['brandName']).flat()))
      let brandObj = []

      brands.forEach((brand) => {
        const outlet = listings.find((list) => list.brandName === brand)

        brandObj.push({
          name: brand,
          logoId: outlet.logoId
        })
      })
      return brandObj
    }

    businessApi.getBusiness(params).then(businessVO => {
      commit('setBusinessVO', businessVO)
      commit('setBusinessBrands', businessBrands(businessVO.listings))
      if (options && options.cb) {
        options.cb()
      }
    })
  },

  updateBusinessId ({ commit }, outletId) {
    commit('updateBusinessId', outletId)
  },

  updateResourceId ({ commit }, resourceId) {
    commit('updateResourceId', resourceId)
  },

  updateQuery ({ commit }, query) {
    let rid = null

    commit('updateQuery', query)
    if (query.resourceId) {
      rid = query.resourceId
    }
    commit('updateResourceId', rid)
  },

  filterOutlet ({ commit }, params) {
    let listingVOs = [];
    let discounts = [];
    commit('updateListingVOs', listingVOs);
    businessApi.filterOutlet(params).then(resp => {
      listingVOs = resp.listingvos;
      commit('updateListingVOs', listingVOs);

      if (Util.isEmpty(listingVOs)) {
        return;
      }
      listingVOs.forEach(ovo => {
        if (!Util.isEmpty(ovo.discounts)) {
          ovo.discounts.forEach(d => {
            d.outlet = {
              buuid: ovo.buuid,
              outletId: ovo.listingId
            }
          })
        }
      })
      discounts = Util.pluck(listingVOs, 'discounts');
      discounts = Util.unique(Util.concatArr(discounts));
      commit('updateListingDiscounts', discounts || []);
    });
  },

  resetListingVOs ({ commit }) {
    commit('updateListingVOs', []);
  },

  resetState({ commit }) {
    commit('resetState')
  },

  deepResetState({ commit }, params) {
    commit('resetStateField', params.clearStateFld)
  },

  fetchOtherOutlets ({ commit, state }) {
    let params = {
      address: state.business.address,
      brandName: state.business.brandName,
      businessId: state.business.buuid
    }
    commit('updateOtherOutlets', []);
    businessApi.filterOutlet(params).then(resp => {
      commit('updateOtherOutlets', resp.listingvos);
    });
  }
}

const mutations = {
  setBusiness (state, business) {
    state.business = business
  },

  updateOutletId (state, outletId) {
    state.outletId = outletId
  },

  updateResourceId (state, resourceId) {
    state.resourceId = resourceId
    if (resourceId) {
      localStorage.setItem('resourceId', resourceId)
    } else {
      localStorage.removeItem('resourceId')
    }
  },

  setBusinessVO (state, businessVO) {
    state.businessVO = businessVO
  },

  updateBusinessId (state, businessId) {
    state.businessId = businessId
  },

  updateQuery (state, query) {
    state.query = query
  },

  setPaymentInfo (state, paymentInfo) {
    state.paymentInfo = paymentInfo
  },

  setBusinessError (state, error) {
    state.businessError = error
  },

  setBusinessBrands (state, businessBrands) {
    state.businessBrands = businessBrands
  },

  setResourceList (state, resourceList) {
    state.resourceList = resourceList
  },

  setFeatures (state, features) {
    state.features = features
  },

  updateListingVOs (state, listingvos) {
    state.listingVOs = listingvos
  },

  updateOtherOutlets (state, otherOutlets) {
    state.otherOutlets = otherOutlets
  },

  updateListingDiscounts (state, discounts) {
    state.listingDiscounts = discounts
  },

  resetState(state) {
    Object.assign(state, defaultState())
  },

  resetStateField(state, fields) {
    fields.forEach(field => {
      state[field] = []
    })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
